/**
 * Free short-form pieces per title — what synced, when, and what an
 * editor needs to fix before a piece looks right on the site.
 *
 * Unpublish takes a piece off the site with a reason: its WordPress post
 * becomes a draft, its address shows a "story unavailable" page with that
 * reason, and a WordPress sync never brings it back. Republish undoes all
 * of it (aamcirc.lab.content.section_unpublish).
 */
import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { Select } from '../components/Field'
import { ApiError, api } from '../api/client'
import { ArchiveSyncBar } from '../lab/ArchiveSyncBar'
import { fmtRelative } from '../lib/format'
import { usePublicBaseUrl } from '../lib/publicBaseUrls'

type Piece = {
  id: number; title: string; slug: string | null; wp_url: string; post_date: string | null
  featured_image_url: string | null; short_teaser: string | null; author_name: string | null
  embeds_count: number; last_synced_at: string | null; status: string
  unpublished_at: string | null; unpublished_by: string | null
  unpublish_reason: string | null; unpublish_note: string | null
  member_twin_id: number | null
}
type Page = { total: number; items: Piece[]; public_base_url: string | null }
type PieceState = { id: number; status: string; wordpress: string }

const REASONS = [
  { value: 'member_archive', label: 'Now in the member archive', hint: 'Links to the member article and names its issue.' },
  { value: 'out_of_date', label: 'Out of date', hint: '"This story has been retired because its information is out of date."' },
  { value: 'unavailable', label: 'No longer available', hint: '"This story is no longer available."' },
  { value: 'custom', label: 'Custom', hint: 'Your own words, one or two sentences.' },
]
const REASON_LABEL: Record<string, string> = Object.fromEntries(REASONS.map((r) => [r.value, r.label]))

const WORDPRESS_RESULT: Record<string, string> = {
  drafted: 'The WordPress post is now a draft.',
  published: 'The WordPress post is published again.',
  already: 'The WordPress post was already in that state.',
  missing: 'There is no WordPress post to change.',
  skipped: 'WordPress was not changed (only production writes to WordPress).',
}

function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    const d = err.detail as { detail?: unknown } | null
    if (d && typeof d.detail === 'string') return d.detail
    return `Failed (error ${err.status}).`
  }
  return 'Failed. Check your connection and try again.'
}

const SECTIONS = [
  { slug: 'flight_journal', title: 'Flight Journal', label: 'Flight Line', path: 'flight-line' },
  { slug: 'rc_car_action', title: 'RC Car Action', label: 'Pit Lane', path: 'pit-lane' },
  { slug: 'model_airplane_news', title: 'Model Airplane News', label: 'Hangar Talk', path: 'hangar-talk' },
  { slug: 'diecast', title: 'Die Cast X', label: 'Display Case', path: 'display-case' },
]

export function SectionsPage() {
  const [pub, setPub] = useState(SECTIONS[0])
  const meBase = usePublicBaseUrl(pub.slug)
  // Query key sits under the ['lab', 'archive', 'list', ...] prefix so
  // ArchiveSyncBar's own completion handler — which invalidates that
  // prefix — refreshes this table too, not just LabArchive's. Without
  // this an editor who runs a sync and doesn't click through one of the
  // outcome counts sees a stale table.
  const q = useQuery({
    queryKey: ['lab', 'archive', 'list', 'sections', pub.slug],
    queryFn: () => api.get<Page>(`/api/lab/archive?publication_slug=${pub.slug}&site_type=main&limit=200`),
  })

  const queryClient = useQueryClient()
  const [unpublishing, setUnpublishing] = useState<number | null>(null)
  const [reason, setReason] = useState('unavailable')
  const [note, setNote] = useState('')
  const [notice, setNotice] = useState<{ id: number; text: string } | null>(null)

  const done = (title: string, verb: string) => (r: PieceState) => {
    setUnpublishing(null)
    setNotice({ id: r.id, text: `${title} ${verb}. ${WORDPRESS_RESULT[r.wordpress] ?? ''}` })
    queryClient.invalidateQueries({ queryKey: ['lab', 'archive', 'list', 'sections'] })
  }
  const unpublish = useMutation({
    mutationFn: (p: Piece) =>
      api.post<PieceState>(`/api/lab/sections/pieces/${p.id}/unpublish`, { reason, note }),
    onSuccess: (r, p) => done(p.title, 'unpublished')(r),
  })
  const republish = useMutation({
    mutationFn: (p: Piece) => api.post<PieceState>(`/api/lab/sections/pieces/${p.id}/republish`),
    onSuccess: (r, p) => done(p.title, 'republished')(r),
  })

  const startUnpublish = (p: Piece) => {
    unpublish.reset()
    setNotice(null)
    setReason(p.member_twin_id ? 'member_archive' : 'unavailable')
    setNote('')
    setUnpublishing(p.id)
  }

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Sections</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            {pub.label}
          </h1>
          <p className="label mt-2 max-w-2xl normal-case tracking-normal">
            Free short-form pieces from {pub.title}'s public site. File a post
            under the &ldquo;{pub.label}&rdquo; category in WordPress and it
            appears here after the next sync.
          </p>
        </div>
        <Select
          value={pub.slug}
          onChange={(e) => setPub(SECTIONS.find((s) => s.slug === e.target.value)!)}
          options={SECTIONS.map((s) => ({ value: s.slug, label: `${s.title} — ${s.label}` }))}
        />
      </header>

      {/*
        The completion-triggered refresh comes from the query key above
        matching ArchiveSyncBar's invalidation prefix, not from these
        callbacks. They exist to open the specific articles behind one
        outcome count (e.g. "3 new") — LabArchive has an ids-view mode for
        that; this page doesn't, so a click here has nowhere useful to go.
        No-ops until this page grows that view.
      */}
      <ArchiveSyncBar
        onShowArticles={() => {}}
        onShowNeedsEnrichment={() => {}}
        onShowNoText={() => {}}
      />

      <Panel label={`${pub.title} — pieces`} bodyClassName="p-0">
        {q.isLoading && <p className="label p-4">Loading…</p>}
        {q.data && (
          <table className="w-full text-sm">
            <thead className="border-b border-ink-500">
              <tr>
                <th className="text-left py-2 pl-4 pr-4 label">Piece</th>
                <th className="text-left py-2 pr-4 label">Byline</th>
                <th className="text-left py-2 pr-4 label">Published</th>
                <th className="text-left py-2 pr-4 label">Synced</th>
                <th className="text-left py-2 pr-4 label">Needs</th>
                <th className="py-2 pr-4 label" />
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((p) => {
                const needs = [
                  !p.featured_image_url && 'image',
                  !p.short_teaser && 'teaser (enrichment pending)',
                ].filter(Boolean).join(', ')
                const isOff = p.status === 'excluded'
                return (
                  <Fragment key={p.id}>
                  <tr className={`border-b border-ink-700 ${isOff ? 'opacity-70' : ''}`}>
                    <td className="py-2 pl-4 pr-4 text-paper">
                      {p.title}
                      {p.embeds_count > 0 && (
                        <span className="ml-2 label text-radar-cyan">▶ {p.embeds_count}</span>
                      )}
                      {isOff && (
                        <div className="mt-1 text-xs">
                          <span className="label-amber">UNPUBLISHED</span>{' '}
                          <span className="text-paper-dim">
                            {REASON_LABEL[p.unpublish_reason ?? ''] ?? 'No reason recorded'}
                            {p.unpublish_reason === 'custom' && p.unpublish_note ? `: “${p.unpublish_note}”` : ''}
                            {p.unpublished_by ? ` · ${p.unpublished_by}` : ''}
                            {p.unpublished_at ? ` · ${fmtRelative(p.unpublished_at)}` : ''}
                          </span>
                        </div>
                      )}
                      {notice?.id === p.id && (
                        <div className="mt-1 text-xs text-radar-green" role="status">{notice.text}</div>
                      )}
                    </td>
                    <td className="py-2 pr-4 label">{p.author_name ?? '—'}</td>
                    <td className="py-2 pr-4 label">{p.post_date ? p.post_date.slice(0, 10) : '—'}</td>
                    <td className="py-2 pr-4 label">{p.last_synced_at ? fmtRelative(p.last_synced_at) : '—'}</td>
                    <td className={`py-2 pr-4 label ${needs ? 'text-amber-glow' : 'text-radar-green'}`}>
                      {needs || 'nothing'}
                    </td>
                    <td className="py-2 pr-4 text-right space-x-3">
                      {p.slug && (q.data.public_base_url ?? meBase) ? (
                        <a
                          className="text-amber-glow underline"
                          href={`${q.data.public_base_url ?? meBase}/${pub.path}/${p.slug}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          view
                        </a>
                      ) : (
                        <span className="text-paper-trace" title="No slug yet — re-sync from WordPress">
                          view
                        </span>
                      )}
                      <a className="text-amber-glow underline" href={p.wp_url} target="_blank" rel="noreferrer">
                        wp
                      </a>
                      {isOff ? (
                        <button
                          type="button"
                          className="text-amber-glow underline disabled:opacity-50"
                          disabled={republish.isPending}
                          onClick={() => {
                            if (window.confirm(`Republish “${p.title}”? It goes back on the site and its WordPress post is published again.`)) {
                              setNotice(null)
                              republish.mutate(p)
                            }
                          }}
                        >
                          republish
                        </button>
                      ) : p.status === 'active' && unpublishing !== p.id ? (
                        <button type="button" className="text-paper-dim underline hover:text-amber-glow"
                                onClick={() => startUnpublish(p)}>
                          unpublish
                        </button>
                      ) : null}
                      {republish.isError && republish.variables?.id === p.id && (
                        <div className="text-xs text-red-400 mt-1 text-right">{errorText(republish.error)}</div>
                      )}
                    </td>
                  </tr>
                  {unpublishing === p.id && (
                    <tr className="border-b border-ink-700 bg-ink-900">
                      <td colSpan={6} className="px-4 py-4">
                        <div className="max-w-2xl space-y-3">
                          <div className="label">
                            Unpublish “{p.title}” — what should its page tell visitors?
                          </div>
                          <div className="space-y-2">
                            {REASONS.map((r) => {
                              const unavailable = r.value === 'member_archive' && !p.member_twin_id
                              return (
                                <label key={r.value}
                                       className={`flex gap-2 items-start ${unavailable ? 'opacity-40' : 'cursor-pointer'}`}>
                                  <input type="radio" name={`reason-${p.id}`} value={r.value}
                                         className="mt-1" disabled={unavailable}
                                         checked={reason === r.value} onChange={() => setReason(r.value)} />
                                  <span>
                                    <span className="text-paper">{r.label}</span>
                                    <span className="block text-xs text-paper-dim">
                                      {unavailable ? 'No member copy of this piece exists.' : r.hint}
                                    </span>
                                  </span>
                                </label>
                              )
                            })}
                          </div>
                          {reason === 'custom' && (
                            <textarea rows={3} maxLength={400} value={note} autoFocus
                                      onChange={(e) => setNote(e.target.value)}
                                      placeholder="e.g. This story was withdrawn at the author's request."
                                      className="w-full bg-ink-900 border border-ink-600 text-paper text-[14px] p-2 font-sans" />
                          )}
                          <p className="text-xs text-paper-dim">
                            The piece leaves {pub.label} and its WordPress post becomes a draft.
                            Its address shows this message with links to the latest pieces.
                            A WordPress sync will not bring it back; use republish.
                          </p>
                          {unpublish.isError && (
                            <p className="text-sm text-red-400">{errorText(unpublish.error)}</p>
                          )}
                          <div className="flex gap-4">
                            <button type="button"
                                    className="label-amber hover:underline disabled:opacity-50"
                                    disabled={unpublish.isPending || (reason === 'custom' && !note.trim())}
                                    onClick={() => unpublish.mutate(p)}>
                              {unpublish.isPending ? 'Unpublishing…' : 'Unpublish'}
                            </button>
                            <button type="button" className="label hover:underline"
                                    disabled={unpublish.isPending}
                                    onClick={() => setUnpublishing(null)}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                )
              })}
              {q.data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-6 text-center label">
                    No pieces synced for {pub.title} yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  )
}
